import { NextPageContext } from 'next'
import { useEffect } from 'react'
import BasicHead from '../../../../../components/common/BasicHead'
import CsrOnlyStudentBrazePixel from '../../../../../components/common/CsrOnlyStudentBrazePixel'
import DeviceSplit from '../../../../../components/common/DeviceSplit'
import LandingNav from '../../../../../components/landing/navigation/LandingNav'
import MobileLandingNav from '../../../../../components/landing/navigation/LandingNav/MobileLandingNav'
import Footer from '../../../../../components/landing/section/footer'
import NewPricing from '../../../../../components/new-portal/pricing/Pricing'
import { getEventUserType, sendGTMBasicPageViewEvent } from '../../../../../helpers/gtmHelper'
import { ISeoData } from '../../../../../model/Common/common.interface'
import { useCustomerType } from '../../../../../modules/apis/student/products'
import { logVisitEvent } from '../../../../../modules/braze/logVisitRecord'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useReturnFullUrl from '../../../../../modules/hooks/webview/common/useReturnFullUrl'
import { LOCALE_KO } from '../../../../../modules/i18n/config'
import { s_head } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { STUDENT_PAGE_VIEW_ID } from '../../../../../modules/ids/gtm/student'
import urls from '../../../../../modules/urls'
import { getOgUrl, t } from '../../../../../modules/utils'

const Pricing = ({ ogData, seoData, isMobileServer }) => {
  const router = useAppRouter()
  const { locale, t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const isMobile = useIsMobile(isMobileServer)
  const variation = 'A'

  useEffect(() => {
    sendGTMBasicPageViewEvent(
      STUDENT_PAGE_VIEW_ID.LANDING_PRICING,
      getEventUserType(currentUser),
      locale,
      variation ? `Landing Pricing - ${variation}` : 'Landing Pricing',
      router.asPath
    )
  }, [])

  const getFullUrl = useReturnFullUrl()
  const { data: customerType, isFetching } = useCustomerType({
    enabled: !!currentUser,
  })
  const isB2CEligible = customerType?.isB2CEligible

  useEffect(() => {
    if (isFetching || !customerType) return

    if (currentUser && !isB2CEligible) {
      router.push(urls.student.newPortal.pricing.business, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: { url: getFullUrl(urls.webview.pricing.business) },
      })
    }
  }, [isFetching, customerType])

  return (
    <div className="bg-white">
      <BasicHead ogData={ogData} seoData={seoData} />
      <CsrOnlyStudentBrazePixel
        currentUser={currentUser}
        callback={(appBoy) =>
          logVisitEvent(appBoy, 'view_landing', {
            property_1: 'pricing',
            locale: locale,
          })
        }
      />

      <DeviceSplit isMobile={isMobile} mobile={<MobileLandingNav />} desktop={<LandingNav />} />

      <NewPricing />
      <Footer />
    </div>
  )
}

Pricing.getInitialProps = async (context: NextPageContext) => {
  const { lang = LOCALE_KO } = context.query

  const ogData = {
    title: t(s_head.student.title, lang),
    desc: t(s_head.student.desc, lang),
    ogUrl: getOgUrl({ lang, context }),
  }

  const seoData: ISeoData = {
    pageTitle: lang === LOCALE_KO ? '성인영어가격 | 링글 일대일 화상영어' : ogData.title,
    pageDescription:
      lang === LOCALE_KO
        ? '화상영어가격 성인영어가격 No.1 일대일 화상영어 링글 스탠퍼드 MBA출신이 만든 1:1 화상 영어 명문대 원어민 튜터의 똑똑한 수업을 경험하세요!'
        : ogData.desc,
    pageKeywords:
      lang === LOCALE_KO
        ? '성인영어가격, 화상영어가격, 화상영어순위, 화상영어, 영어회화, 전화영어, 영어문장, 성인영어회화, 원어민화상영어, 영어회화책, 성인화상영어, 영어회화공부, 영어회화스터디, 비즈니스영어, 영어잘하는법, 기본영어회화, 영국영어, 직장인영어회화, 비지니스영어, 원어민영어회화, 전화영어회화, 비즈니스영어회화, 화상영어사이트, 영어회화학원, 성인영어회화학원'
        : null,
  }

  let userAgent: string

  if (context.req) {
    // if you are on the server and you get a 'req' property from your context
    userAgent = context.req.headers['user-agent'] // get the user-agent from the headers
  } else {
    userAgent = navigator.userAgent // if you are on the client you can access the navigator from the window object
  }

  const isMobileServer = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i))

  return {
    ogData,
    seoData,
    isMobileServer,
  }
}

export default Pricing
